import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { apiRequest } from '../Utils/apiClient';
import { useSnackbar } from '../Utils/SnackBarContext';

/**
 * useCaseDelete Hook
 *
 * Encapsulates case deletion logic with HTTP transport, dialog state, and user feedback.
 *
 * Features:
 * - Manages delete dialog state
 * - Deletes the project through the HTTP API
 * - Shows appropriate snackbar messages
 * - Optional navigation after deletion
 * - Optional custom callback after successful deletion
 *
 * @param {function} onDeleteSuccess - Optional callback after successful deletion
 * @param {boolean} navigateOnDelete - Whether to navigate to home after deleting current case
 * @param {string} currentCaseId - Current case ID (for navigation check)
 *
 * @returns {object} Object containing dialog state and handlers
 */
export const useCaseDelete = ({
  onDeleteSuccess = null,
  navigateOnDelete = false,
  currentCaseId = null
}) => {
  const { t } = useTranslation();
  const { openSnackbar } = useSnackbar();
  const navigate = useNavigate();

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [caseToDelete, setCaseToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  /**
   * Opens the delete confirmation dialog for a specific case
   */
  const openDeleteDialog = useCallback((caseData) => {
    console.log('Opening delete dialog for case:', caseData);
    setCaseToDelete(caseData);
    setDeleteDialogOpen(true);
  }, []);

  /**
   * Closes the delete dialog and clears state
   */
  const closeDeleteDialog = useCallback(() => {
    setDeleteDialogOpen(false);
    setCaseToDelete(null);
  }, []);

  /**
   * Handles the delete response from the server or HTTP API.
   */
  const handleDeleteResponse = useCallback((success, deletedCaseId = null, errorMessage = null) => {
    setIsDeleting(false);

    if (success) {
      openSnackbar(
        t('sidebar.delete_case_success') || 'Case deleted successfully',
        3000,
        'success'
      );

      console.log(
        'handleDeleteResponse: checking navigation. navigateOnDelete:',
        navigateOnDelete,
        'currentCaseId:',
        currentCaseId,
        'deletedCaseId:',
        deletedCaseId
      );
      if (navigateOnDelete && currentCaseId && String(deletedCaseId) === String(currentCaseId)) {
        console.log('Navigating to home...');
        navigate('/');
      }
    } else {
      openSnackbar(
        errorMessage || t('sidebar.delete_case_error') || 'Failed to delete case',
        3000,
        'error'
      );
    }

    closeDeleteDialog();
  }, [closeDeleteDialog, currentCaseId, navigate, navigateOnDelete, openSnackbar, t]);

  /**
   * Confirms and executes the case deletion through the HTTP API.
   */
  const confirmDelete = useCallback(async () => {
    const deletedCase = caseToDelete;
    console.log('Delete confirmed for case:', deletedCase);

    if (!deletedCase) {
      console.log('No case selected for delete');
      return;
    }

    if (isDeleting) {
      console.log('Delete already in progress');
      return;
    }

    setIsDeleting(true);

    try {
      await apiRequest(`/api/v1/projects/${deletedCase.id}`, {
        method: 'DELETE',
      });

      if (onDeleteSuccess) {
        try {
          onDeleteSuccess(deletedCase);
        } catch (callbackError) {
          console.error('Error running delete success callback:', callbackError);
        }
      }

      if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
        try {
          window.dispatchEvent(
            new CustomEvent('dashboard:case-deleted', {
              detail: {
                case: deletedCase,
                caseId: deletedCase.id,
              },
            })
          );
        } catch (eventError) {
          console.error('Error notifying delete listeners:', eventError);
        }
      }

      handleDeleteResponse(true, deletedCase.id);
    } catch (error) {
      console.error('Error deleting case:', error);
      handleDeleteResponse(false, deletedCase.id, error?.message || null);
    }
  }, [caseToDelete, handleDeleteResponse, isDeleting, onDeleteSuccess]);

  return {
    deleteDialogOpen,
    caseToDelete,
    openDeleteDialog,
    closeDeleteDialog,
    confirmDelete,
    isDeleting
  };
};
